import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Badge,
  Button,
  DataTable,
  EmptyState,
  FilterBar,
  PageContainer,
  PageHeader,
  Pagination,
  Spinner,
} from '../../ui/components';
import { Icon } from '../../ui/icons';
import { useProperty } from '../../app/PropertyContext';
import { useT } from '../../i18n';
import { buildFilterParams } from '../../lib/filters';
import { catalogApi } from '../catalog/catalogApi';
import { marketlistApi, mlStatusMeta, marketListPath, ML_STATUSES } from './marketlistApi';
import './marketlist.css';

const PAGE_SIZE = 25;

export default function MarketListPage() {
  const navigate = useNavigate();
  const t = useT();
  const { hasPermission, activePropertyId, properties } = useProperty();
  const canCreate = hasPermission('marketlist.create');

  const [data, setData] = useState({ items: [], totalCount: 0, page: 1, pageSize: PAGE_SIZE });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchText, setSearchText] = useState('');
  const [search, setSearch] = useState('');
  const [values, setValues] = useState({});
  const [locations, setLocations] = useState([]);
  const [page, setPage] = useState(1);
  const debounceRef = useRef(null);

  useEffect(() => {
    let active = true;
    catalogApi.listLocations(activePropertyId).then((res) => { if (active) setLocations(res || []); }).catch(() => {});
    return () => { active = false; };
  }, [activePropertyId]);

  const locationName = useCallback((id) => locations.find((l) => l.id === id)?.name || '—', [locations]);

  const fields = useMemo(() => [
    { id: 'statuses', label: t('Status'), type: 'multiselect', options: ML_STATUSES.map((s) => ({ value: s.value, label: t(s.label) })) },
    { id: 'locationIds', label: t('Outlet'), type: 'multiselect', searchable: true, options: locations.map((l) => ({ value: l.id, label: l.name })) },
    { id: 'market', label: t('Market date'), type: 'daterange', fromParam: 'marketFrom', toParam: 'marketTo' },
  ], [locations, t]);

  const filterParams = useMemo(() => buildFilterParams(fields, values), [fields, values]);
  const hasFilters = search !== '' || Object.keys(filterParams).length > 0;

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await marketlistApi.list({ page, pageSize: PAGE_SIZE, search, ...filterParams });
      setData(res);
    } catch (err) {
      setError(err?.message || t('Failed to load market lists.'));
    } finally {
      setLoading(false);
    }
  }, [page, search, filterParams, t]);

  useEffect(() => { load(); }, [load]);

  const onSearchChange = (value) => {
    setSearchText(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => { setPage(1); setSearch(value); }, 300);
  };

  const onFilterChange = (id, value) => {
    setPage(1);
    setValues((prev) => {
      const next = { ...prev };
      if (value === undefined) delete next[id];
      else next[id] = value;
      return next;
    });
  };

  const open = (row) => navigate(marketListPath(row));

  const propertyName = (id) => properties.find((p) => p.id === id)?.name;

  const columns = [
    {
      key: 'number', header: t('Number'), width: 130,
      render: (r) => <span style={{ fontFamily: 'var(--font-mono)' }}>{r.number}</span>,
    },
    // In "All properties" view, show which property each list belongs to.
    ...(!activePropertyId ? [{
      key: 'property', header: t('Property'), width: 160,
      render: (r) => propertyName(r.propertyId) || <span className="pp-cell-muted">—</span>,
    }] : []),
    {
      key: 'title', header: t('Title'), width: 280,
      render: (r) => (
        <div>
          <div style={{ fontWeight: 600 }}>{r.title}</div>
          <div className="pp-cell-muted" style={{ fontSize: 'var(--text-xs)' }}>{r.createdByName || '—'}</div>
        </div>
      ),
    },
    {
      key: 'marketDate', header: t('Market date'), width: 130,
      render: (r) => (r.marketDate ? new Date(r.marketDate).toLocaleDateString() : <span className="pp-cell-muted">—</span>),
    },
    { key: 'location', header: t('Outlet'), width: 180, render: (r) => locationName(r.locationId) },
    {
      key: 'status', header: t('Status'), width: 140,
      render: (r) => { const m = mlStatusMeta(r.status); return <Badge tone={m.tone} dot>{t(m.label)}</Badge>; },
    },
    { key: 'lines', header: t('Lines'), width: 80, align: 'right', render: (r) => r.lineCount },
  ];

  const totalPages = Math.max(1, Math.ceil(data.totalCount / PAGE_SIZE));

  return (
    <PageContainer size="full">
      <PageHeader
        title={t('Market list')}
        description={t('Daily fresh-produce ordering sheets — compare vendor quotes and finalize into requisitions.')}
        icon={<Icon.ClipboardList size={22} />}
        actions={canCreate ? (
          <Button variant="primary" leadingIcon={<Icon.Plus size={16} />} onClick={() => navigate('/market-list/new')}>
            {t('New market list')}
          </Button>
        ) : null}
      />

      <FilterBar
        searchPlaceholder={t('Search by number, title or creator…')}
        searchValue={searchText}
        onSearchChange={onSearchChange}
        fields={fields}
        values={values}
        onChange={onFilterChange}
      />

      {error ? (
        <EmptyState
          icon={<Icon.CircleAlert size={28} />}
          title={t("Couldn't load market lists")}
          description={error}
          action={<Button variant="secondary" onClick={load}>{t('Retry')}</Button>}
        />
      ) : loading ? (
        <div style={{ display: 'grid', placeItems: 'center', padding: 'var(--space-16)' }}><Spinner size={26} /></div>
      ) : (
        <>
          <DataTable
            columns={columns}
            data={data.items}
            rowKey={(r) => r.id}
            density="comfortable"
            onRowClick={open}
            emptyState={
              <EmptyState
                icon={<Icon.ClipboardList size={28} />}
                title={hasFilters ? t('No matching market lists') : t('No market lists yet')}
                description={hasFilters ? t('Try adjusting your search or filters.') : t('Build your first daily market list to capture vendor quotes against par levels.')}
                action={!hasFilters && canCreate ? (
                  <Button variant="primary" leadingIcon={<Icon.Plus size={16} />} onClick={() => navigate('/market-list/new')}>{t('New market list')}</Button>
                ) : null}
              />
            }
          />
          {data.totalCount > 0 && (
            <div style={{ marginTop: 'var(--space-4)' }}>
              <Pagination page={page} pageCount={totalPages} onPageChange={setPage} totalItems={data.totalCount} pageSize={PAGE_SIZE} />
            </div>
          )}
        </>
      )}
    </PageContainer>
  );
}
